import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, BookOpen, History, PlayCircle, Wallet, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API, { getMediaUrl } from "../services/api";

const CHIP_AMOUNTS = [10, 50, 100, 500, 1000, 5000];
const MAX_BET = 5000;
const GAME_TABS = [
  { key: "parity", label: "Parity" },
  { key: "sapre", label: "Sapre" },
  { key: "bcone", label: "Bcone" },
  { key: "emerd", label: "Emerd" },
];
const VIEW_TABS = [
  { key: "play", label: "Play", icon: PlayCircle },
  { key: "history", label: "Bet History", icon: History },
  { key: "rules", label: "Rules", icon: BookOpen },
];
const CHIP_IMAGE_10 = "/ChatGPT Image May 15, 2026, 11_15_30 PM (4).png";
const CHIP_IMAGE_50 = "/ChatGPT Image May 15, 2026, 11_15_29 PM (1).png";
const CHIP_IMAGE_100 = "/ChatGPT Image May 15, 2026, 11_15_29 PM (2).png";
const CHIP_IMAGE_500 = "/ChatGPT Image May 15, 2026, 11_15_30 PM (3).png";
const CHIP_IMAGE_1000 = "/ChatGPT Image May 15, 2026, 11_15_31 PM (5).png";
const CHIP_IMAGE_5000 = "/ChatGPT Image May 15, 2026, 11_15_31 PM (6).png";
const CHIP_SOUND = "/sounds/oxidvideos-placing-poker-chips-522515.mp3";
const UNDO_SOUND = "/sounds/freesound_community-button-pressed-38129.mp3";
const SUBMIT_SOUND = "/sounds/humordome-magic-button-click-453258.mp3";

function getChipImage(value) {
  if (value === 10) return CHIP_IMAGE_10;
  if (value === 50) return CHIP_IMAGE_50;
  if (value === 100) return CHIP_IMAGE_100;
  if (value === 500) return CHIP_IMAGE_500;
  if (value === 1000) return CHIP_IMAGE_1000;
  if (value === 5000) return CHIP_IMAGE_5000;
  return CHIP_IMAGE_10;
}

function formatHistoryDateTime(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatColorBetStatus(status) {
  const value = String(status || "pending").toLowerCase();
  if (value === "pending") return "BET SUCCESS - RESULT PENDING";
  if (value === "won") return "WON";
  if (value === "lost") return "LOST";
  return value.toUpperCase();
}

function getIndiaDateKey(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(parsed);
  const pick = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${pick("year")}-${pick("month")}-${pick("day")}`;
}

function ColorGame() {
  const navigate = useNavigate();
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);

  const [activeMode, setActiveMode] = useState("parity");
  const [activeView, setActiveView] = useState("play");
  const [state, setState] = useState({ history: [], my_bets: [], current_round: null, balance: 0, rules: {} });
  const [selectedChip, setSelectedChip] = useState(10);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [modeSwitching, setModeSwitching] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState(Date.now());
  const [nowTick, setNowTick] = useState(Date.now());
  const [betToast, setBetToast] = useState("");
  const [pendingBets, setPendingBets] = useState([]);
  const [submittingSlip, setSubmittingSlip] = useState(false);
  const [winToast, setWinToast] = useState(null);
  const [settings, setSettings] = useState({});
  const [historyDate, setHistoryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const colorGameLogo = resolveAssetUrl(settings.asset_color_game_logo_url, "/mk-logo192.png");

  const toastTimerRef = useRef(null);
  const winToastTimerRef = useRef(null);
  const chipTapRef = useRef({ value: null, time: 0 });
  const chipAudioRef = useRef(null);
  const undoAudioRef = useRef(null);
  const submitAudioRef = useRef(null);
  const seenWinIdsRef = useRef(new Set());
  const winsPrimedRef = useRef(false);

  const loadState = useCallback(async () => {
    if (!user?.id) return;
    const res = await API.get(`/color/state?user_id=${user.id}&mode=${activeMode}&date=${historyDate}`);
    setState(res.data || {});
    setLoadedAt(Date.now());
  }, [activeMode, historyDate, user?.id]);

  useEffect(() => {
    API.get("/settings/public").then((res) => setSettings(res.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    loadState().catch(() => setMessage("Unable to load Color Prediction right now."));
    const poll = setInterval(() => loadState().catch(() => {}), 3000);
    const clock = setInterval(() => setNowTick(Date.now()), 250);
    return () => {
      clearInterval(poll);
      clearInterval(clock);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (winToastTimerRef.current) clearTimeout(winToastTimerRef.current);
    };
  }, [loadState]);

  useEffect(() => {
    const audio = new Audio(CHIP_SOUND);
    audio.preload = "auto";
    audio.volume = 0.65;
    chipAudioRef.current = audio;
    const undoAudio = new Audio(UNDO_SOUND);
    undoAudio.preload = "auto";
    undoAudio.volume = 0.6;
    undoAudioRef.current = undoAudio;
    const submitAudio = new Audio(SUBMIT_SOUND);
    submitAudio.preload = "auto";
    submitAudio.volume = 0.7;
    submitAudioRef.current = submitAudio;
  }, []);

  useEffect(() => {
    setMessage("");
    setActiveView("play");
    setPendingBets([]);
    setSubmittingSlip(false);
    setShowAllHistory(false);
  }, [activeMode]);

  useEffect(() => {
    setModeSwitching(true);
    const timerHandle = setTimeout(() => setModeSwitching(false), 180);
    return () => clearTimeout(timerHandle);
  }, [activeMode]);

  const remainingMs = Math.max(0, Number(state.current_round?.remaining_ms || 0) - Math.max(0, nowTick - loadedAt));
  const lockMs = Number(state.current_round?.lock_ms || 10000);
  const lockSeconds = Number(state.rules?.lock_seconds || Math.floor(lockMs / 1000) || 10);
  const seconds = Math.ceil(remainingMs / 1000);
  const countdown = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  const bettingLocked = remainingMs <= lockMs;
  const walletAmount = Number(state.balance || 0).toFixed(2);
  const payouts = state.rules?.payouts || {};
  const pendingTotal = useMemo(
    () => pendingBets.reduce((sum, bet) => sum + Number(bet.amount || 0), 0),
    [pendingBets]
  );
  const pendingStakeMap = useMemo(() => {
    const map = new Map();
    pendingBets.forEach((bet) => {
      const key = `${bet.bet_type}:${bet.selection}`;
      map.set(key, Number(map.get(key) || 0) + Number(bet.amount || 0));
    });
    return map;
  }, [pendingBets]);

  const showToast = (text) => {
    setBetToast(text);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setBetToast(""), 2200);
  };

  const playChipSound = () => {
    if (!chipAudioRef.current) return;
    chipAudioRef.current.currentTime = 0;
    chipAudioRef.current.play().catch(() => {});
  };

  const playUndoSound = () => {
    if (!undoAudioRef.current) return;
    undoAudioRef.current.currentTime = 0;
    undoAudioRef.current.play().catch(() => {});
  };

  const playSubmitSound = () => {
    if (!submitAudioRef.current) return;
    submitAudioRef.current.currentTime = 0;
    submitAudioRef.current.play().catch(() => {});
  };

  const playWinSound = () => {
    try {
      const audio = new Audio("/mk_cashout_register.mp3");
      audio.volume = 0.8;
      audio.currentTime = 0;
      audio.play().catch(() => {});
    } catch {}
  };

  const formatSelectionLabel = (betType, selection) => {
    if (betType === "number") return `Number ${selection}`;
    if (betType === "size") return selection === "small" ? "Small" : "Big";
    return String(selection).charAt(0).toUpperCase() + String(selection).slice(1);
  };

  const handleChipTap = (value) => {
    const now = Date.now();
    const isDoubleTap = chipTapRef.current.value === value && now - chipTapRef.current.time < 350;
    chipTapRef.current = { value, time: now };
    if (isDoubleTap) {
      setSelectedChip((current) => Math.min(MAX_BET, current + value));
      return;
    }
    setSelectedChip(value);
  };

  const addPendingBet = (betType, selection) => {
    if (!user?.id) return navigate("/login");
    if (bettingLocked) {
      setMessage("Betting locked for this round.");
      return;
    }
    if (submittingSlip) return;
    if (pendingTotal + selectedChip > MAX_BET) {
      setMessage(`Maximum total bet is Rs ${MAX_BET}.`);
      return;
    }
    const label = formatSelectionLabel(betType, selection);
    setPendingBets((prev) => {
      const existingIndex = prev.findIndex((bet) => bet.bet_type === betType && bet.selection === selection);
      if (existingIndex === -1) {
        return [
          ...prev,
          {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            bet_type: betType,
            selection,
            amount: selectedChip,
            label,
          },
        ];
      }
      return prev.map((bet, index) => (
        index === existingIndex
          ? { ...bet, amount: Number((Number(bet.amount || 0) + selectedChip).toFixed(2)) }
          : bet
      ));
    });
    playChipSound();
    showToast(`${label} added · Rs ${selectedChip.toFixed(2)}`);
    setMessage("");
  };

  const undoLastBet = () => {
    if (bettingLocked || submittingSlip || !pendingBets.length) return;
    playUndoSound();
    setPendingBets((prev) => prev.slice(0, -1));
  };

  const submitPendingBets = useCallback(async () => {
    if (!user?.id || !pendingBets.length || submittingSlip) return;
    const currentPeriod = state.current_round?.period || "";
    if (!currentPeriod) return;
    setSubmittingSlip(true);
    setLoading(true);
    try {
      const res = await API.post("/color/bet/batch", {
        user_id: user.id,
        mode: activeMode,
        bets: pendingBets.map((bet) => ({
          bet_type: bet.bet_type,
          selection: bet.selection,
          amount: bet.amount,
        })),
      });
      setState((prev) => ({ ...prev, balance: res.data?.balance ?? prev.balance }));
      playSubmitSound();
      showToast(`Rs ${pendingTotal.toFixed(2)} locked for ${pendingBets.length} bets`);
      setMessage(`Round locked. Rs ${pendingTotal.toFixed(2)} submitted.`);
      setPendingBets([]);
      await loadState();
    } catch (err) {
      setMessage(err.response?.data?.message || "Unable to lock bets. Try again next round.");
    } finally {
      setSubmittingSlip(false);
      setLoading(false);
    }
  }, [activeMode, loadState, pendingBets, pendingTotal, state.current_round?.period, submittingSlip, user?.id]);

  useEffect(() => {
    const wins = Array.isArray(state.my_bets)
      ? state.my_bets.filter((bet) => String(bet.status || "").toLowerCase() === "won" && Number(bet.payout || 0) > 0)
      : [];

    if (!winsPrimedRef.current) {
      wins.forEach((bet) => {
        const winKey = `${bet.id}:${bet.settled_at || bet.created_at || ""}`;
        seenWinIdsRef.current.add(winKey);
      });
      winsPrimedRef.current = true;
      return;
    }

    const freshWins = wins
      .filter((bet) => {
        const winKey = `${bet.id}:${bet.settled_at || bet.created_at || ""}`;
        const settledTime = new Date(bet.settled_at || bet.created_at || 0).getTime();
        const isRecent = settledTime > 0 && Date.now() - settledTime < 25000;
        return !seenWinIdsRef.current.has(winKey) && isRecent;
      })
      .sort((a, b) => new Date(b.settled_at || b.created_at || 0).getTime() - new Date(a.settled_at || a.created_at || 0).getTime());

    if (!freshWins.length) return;

    freshWins.forEach((bet) => {
      const winKey = `${bet.id}:${bet.settled_at || bet.created_at || ""}`;
      seenWinIdsRef.current.add(winKey);
    });

    const latestWin = freshWins[0];
    setWinToast({
      id: latestWin.id,
      amount: Number(latestWin.payout || 0),
      number: latestWin.result_number,
      color: latestWin.result_color,
      label: formatSelectionLabel(latestWin.bet_type, latestWin.selection),
    });
    playWinSound();
    if (winToastTimerRef.current) clearTimeout(winToastTimerRef.current);
    winToastTimerRef.current = setTimeout(() => setWinToast(null), 5200);
  }, [state.my_bets]);

  useEffect(() => {
    if (!pendingBets.length || !state.current_round?.period) return;
    if (remainingMs <= lockMs + 900 && remainingMs > lockMs && !submittingSlip) {
      submitPendingBets().catch(() => {});
    }
  }, [lockMs, pendingBets.length, remainingMs, state.current_round?.period, submitPendingBets, submittingSlip]);

  const topResults = (state.history || []).slice(0, 100);
  const getPendingStake = (betType, selection) => Number(pendingStakeMap.get(`${betType}:${selection}`) || 0);
  const todayHistoryBets = useMemo(() => {
    const list = Array.isArray(state.my_bets) ? state.my_bets : [];
    const filtered = list.filter((bet) => getIndiaDateKey(bet.created_at || bet.settled_at || "") === historyDate);
    const grouped = new Map();

    filtered.forEach((bet) => {
      const createdAt = bet.created_at || bet.settled_at || "";
      const key = [bet.period, bet.bet_type, bet.selection, String(createdAt).slice(0, 10)].join("|");
      const existing = grouped.get(key);
      if (existing) {
        existing.amount += Number(bet.amount || 0);
        existing.payout += Number(bet.payout || 0);
        existing.count += 1;
        existing.ids.push(bet.id);
        if (bet.wallet_balance !== null && bet.wallet_balance !== undefined) {
          existing.wallet_balance = Number(bet.wallet_balance || 0);
        }
        if (new Date(createdAt).getTime() > new Date(existing.created_at || 0).getTime()) {
          existing.created_at = createdAt;
          existing.status = bet.status;
          existing.settled_at = bet.settled_at;
        }
        return;
      }

      grouped.set(key, {
        id: bet.id,
        ids: [bet.id],
        period: bet.period,
        bet_type: bet.bet_type,
        selection: bet.selection,
        amount: Number(bet.amount || 0),
        payout: Number(bet.payout || 0),
        status: bet.status,
        created_at: createdAt,
        settled_at: bet.settled_at,
        wallet_balance: bet.wallet_balance,
        count: 1,
      });
    });

    return Array.from(grouped.values()).sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
  }, [state.my_bets, historyDate]);
  const visibleHistoryBets = useMemo(
    () => (showAllHistory ? todayHistoryBets : todayHistoryBets.slice(0, 10)),
    [showAllHistory, todayHistoryBets]
  );

  return (
    <main style={page}>
      <header style={topbar}>
        <button type="button" style={backButton} onClick={() => navigate("/dashboard")}>
          <ArrowLeft size={22} />
        </button>
        <div style={titleWrap}>
          <h1 style={title}>Color Prediction Game</h1>
          <p style={subtitle}>60s round · {lockSeconds}s lock</p>
        </div>
        <div style={wallet}>
          <Wallet size={14} />
          <span>Rs {walletAmount}</span>
        </div>
      </header>

      {winToast ? (
        <div style={winToastBox}>
          <div style={winToastLeft}>
            <span>You Won!</span>
            <b>#{winToast.number} {String(winToast.color || "").replace(/,/g, " + ")}</b>
            <small>{winToast.label}</small>
          </div>
          <div style={winToastRight}>
            <span>Win INR</span>
            <b>{winToast.amount.toFixed(2)}</b>
          </div>
          <button type="button" style={winToastClose} onClick={() => setWinToast(null)}>x</button>
        </div>
      ) : null}

      {betToast ? <div style={{ ...toast, top: winToast ? "148px" : "76px" }}>{betToast}</div> : null}

      <section style={{ ...gameCard, ...(modeSwitching ? gameCardSwitching : {}) }}>
        <div style={tabs}>
          {GAME_TABS.map((tab) => (
            <button key={tab.key} type="button" style={tab.key === activeMode ? activeTab : tabPill} onClick={() => setActiveMode(tab.key)}>
              {tab.label}
            </button>
          ))}
        </div>

        <div style={topResultsCard}>
          <div style={topResultsHeader}>
            <strong style={topResultsTitle}>Last 100 Results</strong>
          </div>
          <div style={topResultsRow}>
            {topResults.map((row) => (
              <div key={row.period} style={resultChip}>
                <span style={chipRound}>{row.period.split("-").pop()}</span>
                <strong style={{ ...chipNumber, color: getNumberTone(row.result_number) }}>{row.result_number}</strong>
                <ResultDots color={row.result_color} />
              </div>
            ))}
          </div>
        </div>

        <div style={viewTabs}>
          {VIEW_TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} type="button" style={tab.key === activeView ? activeViewTab : viewTab} onClick={() => setActiveView(tab.key)}>
                <Icon size={15} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {activeView === "play" && (
          <>
            <div style={periodRow}>
              <div style={roundInfoCard}>
                <span style={label}>Current Round</span>
                <strong style={period}>{state.current_round?.period || "-"}</strong>
              </div>
              <div style={bettingLocked ? lockedTimerCard : timerCard}>
                <span style={timerLabel}>Result In</span>
                <strong style={timer}>{countdown}</strong>
                <span style={bettingLocked ? lockedTimerMeta : timerMeta}>{bettingLocked ? "Locked" : "Open"}</span>
              </div>
            </div>

            <div style={joinRow}>
              <button disabled={loading || bettingLocked || submittingSlip} type="button" style={{ ...joinButton, background: greenButtonBg }} onClick={() => addPendingBet("color", "green")}>
                <span>Green</span>
                <span style={buttonMeta}>Pay {payouts.red_green || 1.95}x</span>
                {getPendingStake("color", "green") > 0 ? <span style={betStamp}>Rs {getPendingStake("color", "green").toFixed(0)}</span> : null}
              </button>
              <button disabled={loading || bettingLocked || submittingSlip} type="button" style={{ ...joinButton, background: violetButtonBg }} onClick={() => addPendingBet("color", "violet")}>
                <span>Violet</span>
                <span style={buttonMeta}>Pay {payouts.violet || 4.5}x</span>
                {getPendingStake("color", "violet") > 0 ? <span style={betStamp}>Rs {getPendingStake("color", "violet").toFixed(0)}</span> : null}
              </button>
              <button disabled={loading || bettingLocked || submittingSlip} type="button" style={{ ...joinButton, background: redButtonBg }} onClick={() => addPendingBet("color", "red")}>
                <span>Red</span>
                <span style={buttonMeta}>Pay {payouts.red_green || 1.95}x</span>
                {getPendingStake("color", "red") > 0 ? <span style={betStamp}>Rs {getPendingStake("color", "red").toFixed(0)}</span> : null}
              </button>
            </div>

            <div style={numberGrid}>
              {Array.from({ length: 10 }, (_, number) => (
                <button key={number} disabled={loading || bettingLocked || submittingSlip} type="button" style={numberButton(number)} onClick={() => addPendingBet("number", String(number))}>
                  <span>{number}</span>
                  <span style={numberMeta}>{payouts.number || 9}x</span>
                  {getPendingStake("number", String(number)) > 0 ? <span style={betStamp}>Rs {getPendingStake("number", String(number)).toFixed(0)}</span> : null}
                </button>
              ))}
            </div>

            <div style={sizeRow}>
              <button disabled={loading || bettingLocked || submittingSlip} type="button" style={{ ...sizeButton, background: goldButtonBg }} onClick={() => addPendingBet("size", "small")}>
                <span>Small 0-4</span>
                <span style={buttonMeta}>Pay {payouts.size || 2}x</span>
                {getPendingStake("size", "small") > 0 ? <span style={betStamp}>Rs {getPendingStake("size", "small").toFixed(0)}</span> : null}
              </button>
              <button disabled={loading || bettingLocked || submittingSlip} type="button" style={{ ...sizeButton, background: blueButtonBg }} onClick={() => addPendingBet("size", "big")}>
                <span>Big 5-9</span>
                <span style={buttonMeta}>Pay {payouts.size || 2}x</span>
                {getPendingStake("size", "big") > 0 ? <span style={betStamp}>Rs {getPendingStake("size", "big").toFixed(0)}</span> : null}
              </button>
            </div>
          </>
        )}

        {activeView === "history" && (
          <section style={recordCard}>
            <div style={sectionTitle}><History size={18} /> My Bets</div>
            <div style={historyToolbar}>
              <input type="date" value={historyDate} onChange={(event) => setHistoryDate(event.target.value)} style={historyDateInput} />
              {todayHistoryBets.length > 10 ? (
                <button type="button" style={historyMoreButton} onClick={() => setShowAllHistory((value) => !value)}>
                  {showAllHistory ? "Show 10" : "More"}
                </button>
              ) : null}
            </div>
            <div style={myBetList}>
              {visibleHistoryBets.map((bet) => (
                <div key={bet.id} style={myBetRow}>
                  <div style={{ display: "grid", gap: "4px" }}>
                    <strong>{formatSelectionLabel(bet.bet_type, bet.selection)}</strong>
                    <span style={mutedText}>{bet.period} · {bet.bet_type} · {formatHistoryDateTime(bet.created_at)}</span>
                  </div>
                  <div style={{ textAlign: "right", display: "grid", gap: "4px" }}>
                    <strong>Rs {Number(bet.amount || 0).toFixed(2)}</strong>
                    <span style={{ ...mutedText, color: bet.status === "won" ? "#16a34a" : bet.status === "lost" ? "#ef4444" : "#f59e0b" }}>
                      {formatColorBetStatus(bet.status)}
                      {Number(bet.payout || 0) > 0 ? ` · +Rs ${Number(bet.payout).toFixed(2)}` : ""}
                    </span>
                    {bet.wallet_balance !== null && bet.wallet_balance !== undefined ? (
                      <span style={mutedText}>Bal Rs {Number(bet.wallet_balance || 0).toFixed(2)}</span>
                    ) : null}
                  </div>
                </div>
              ))}
              {!visibleHistoryBets.length && <div style={empty}>No bets found today.</div>}
            </div>
          </section>
        )}

        {activeView === "rules" && (
          <section style={recordCard}>
            <div style={sectionTitle}><BookOpen size={18} /> Rules & Payouts</div>
            <div style={rulesGrid}>
              <div style={ruleBox}><strong>Round Format</strong><span>{state.rules?.round_seconds || 60}s total. Last {lockSeconds}s locked.</span></div>
              <div style={ruleBox}><strong>Color Mapping</strong><span>0 = Red + Violet · 5 = Green + Violet · 1,3,7,9 = Green · 2,4,6,8 = Red</span></div>
              <div style={ruleBox}><strong>Payouts</strong><span>Red / Green {payouts.red_green || 1.95}x · Violet {payouts.violet || 4.5}x · Number {payouts.number || 9}x · Big/Small {payouts.size || 2}x</span></div>
              <div style={ruleBox}><strong>Special</strong><span>0 => Red {payouts.zero_red || 1.5}x · 5 => Green {payouts.five_green || 1.5}x</span></div>
              <div style={ruleBoxWide}><strong>Example</strong><span>Result 0: Rs 100 on Number 0 = Rs 900, Rs 100 on Violet = Rs 450, Rs 100 on Red = Rs 150. Total = Rs 1500 when all three were placed separately.</span></div>
            </div>
          </section>
        )}

        {bettingLocked && activeView === "play" ? <div style={lockNote}>Betting locked for this round.</div> : null}
        {submittingSlip ? <div style={messageBox}>Locking your pending bets now...</div> : null}
        {message ? <div style={messageBox}>{message}</div> : null}
      </section>

      {activeView === "play" ? (
        <section style={chipDockShell}>
        <div style={chipFooter}>
          <div style={chipFooterTop}>
            <button type="button" style={undoButton} disabled={!pendingBets.length || bettingLocked || submittingSlip} onClick={undoLastBet}>
              <RotateCcw size={14} />
              <span>Undo Last</span>
            </button>
            <div style={footerTotalBox}>
              <span style={footerTotalLabel}>Total Bet</span>
              <strong style={footerTotalValue}>Rs {pendingTotal.toFixed(2)}</strong>
            </div>
          </div>
          <button
            type="button"
            style={!pendingBets.length || bettingLocked || submittingSlip ? submitButtonDisabled : submitButton}
            disabled={!pendingBets.length || bettingLocked || submittingSlip}
            onClick={() => submitPendingBets().catch(() => {})}
          >
            {submittingSlip ? "Submitting..." : `Submit Bets · Rs ${pendingTotal.toFixed(2)}`}
          </button>
          <div style={chipScroller}>
            {CHIP_AMOUNTS.map((value) => (
              <button
                key={value}
                type="button"
                style={{
                  ...chipButton,
                  ...(selectedChip === value ? activeChipButton : {}),
                }}
                onClick={() => handleChipTap(value)}
              >
                <img src={getChipImage(value)} alt="" style={chipImage} />
                <span style={srOnly}>{value}</span>
              </button>
            ))}
          </div>
          <div style={poweredBy}>
            <img src={colorGameLogo} alt="MK Casino" style={poweredLogo} />
            <span style={poweredText}>Powered by <strong>MK CASINO</strong></span>
          </div>
        </div>
        </section>
      ) : null}
    </main>
  );
}

function ResultDots({ color }) {
  const parts = String(color || "").split(",").filter(Boolean);
  return (
    <span style={dotRow}>
      {parts.map((item) => <span key={item} style={{ ...dot, background: dotColor(item) }} />)}
    </span>
  );
}

function dotColor(color) {
  if (color === "green") return "#22c55e";
  if (color === "violet") return "#8b5cf6";
  return "#ef4444";
}

function getNumberTone(number) {
  const value = Number(number);
  if (value === 0) return "#ef4444";
  if (value === 5) return "#16a34a";
  return value % 2 === 0 ? "#ef4444" : "#16a34a";
}

const numberButton = (number) => ({
  position: "relative",
  border: 0,
  borderRadius: "10px",
  minHeight: "46px",
  color: "#fff",
  fontWeight: 900,
  fontSize: "17px",
  background: number === 0
    ? "linear-gradient(145deg,#ff8a8a 0%,#ef4444 42%,#7c3aed 58%,#4c1d95 100%)"
    : number === 5
      ? "linear-gradient(145deg,#8cffb3 0%,#16a34a 42%,#8b5cf6 58%,#4c1d95 100%)"
      : number % 2 === 0
        ? redButtonBg
        : greenButtonBg,
  boxShadow: "inset 0 2px 4px rgba(255,255,255,.28), inset 0 -5px 9px rgba(0,0,0,.18), 0 8px 16px rgba(15,23,42,.16)",
  display: "grid",
  gap: "2px",
  placeItems: "center",
  padding: "6px",
});

const page = { minHeight: "100vh", background: "linear-gradient(180deg,#f4f7fb 0%,#f5f7fb 72%,#f8fafc 100%)", color: "#111827", padding: "9px 9px 12px", display: "grid", gap: "7px", alignContent: "start" };
const topbar = { display: "grid", gridTemplateColumns: "38px minmax(0,1fr) auto", alignItems: "center", gap: "8px", background: "linear-gradient(180deg,#fffaf0,#f8efd9)", borderRadius: "14px", padding: "7px 9px", boxShadow: "0 8px 22px rgba(15,23,42,.08)" };
const backButton = { width: "38px", height: "38px", border: 0, borderRadius: "10px", background: "#f1f5f9", display: "grid", placeItems: "center" };
const titleWrap = { minWidth: 0 };
const title = { margin: 0, fontSize: "17px", fontWeight: 900, background: "linear-gradient(90deg,#f59e0b 0%,#22c55e 32%,#8b5cf6 66%,#ef4444 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", letterSpacing: ".1px" };
  const subtitle = { margin: "1px 0 0", color: "#8b6b1a", fontSize: "10px", fontWeight: 700 };
const wallet = { display: "inline-flex", alignItems: "center", gap: "6px", padding: "7px 10px", borderRadius: "999px", background: "linear-gradient(180deg,#fff4c2 0%,#f0cf65 55%,#b78618 100%)", color: "#5b3b00", fontWeight: 900, fontSize: "13px", whiteSpace: "nowrap", boxShadow: "inset 0 1px 3px rgba(255,255,255,.35)" };
const toast = { position: "fixed", left: "50%", top: "76px", transform: "translateX(-50%)", zIndex: 60, width: "min(420px, calc(100vw - 28px))", pointerEvents: "none", background: "linear-gradient(135deg,#101827,#233047)", color: "#fff", borderRadius: "14px", padding: "10px 13px", fontWeight: 900, fontSize: "13px", textAlign: "center", boxShadow: "0 18px 36px rgba(15,23,42,.32)", border: "1px solid rgba(255,255,255,.10)" };
const winToastBox = { position: "fixed", left: "50%", top: "76px", transform: "translateX(-50%)", zIndex: 70, width: "min(460px, calc(100vw - 26px))", minHeight: "76px", borderRadius: "22px", padding: "10px 12px", display: "grid", gridTemplateColumns: "minmax(0,1fr) auto 28px", alignItems: "center", gap: "10px", color: "#fff", background: "linear-gradient(135deg,#11351c 0%,#16522c 18%,#295a7a 45%,#63218b 70%,#b81a34 100%)", boxShadow: "0 18px 38px rgba(15,23,42,.28)", border: "1px solid rgba(255,255,255,.16)", animation: "cashoutToastFloat 5.2s ease both" };
const winToastLeft = { display: "grid", gap: "2px", minWidth: 0 };
const winToastRight = { minWidth: "112px", borderRadius: "18px", padding: "10px 14px", background: "linear-gradient(180deg,rgba(255,255,255,.18),rgba(255,255,255,.08))", display: "grid", gap: "2px", textAlign: "right" };
const winToastClose = { width: "28px", height: "28px", border: 0, borderRadius: "999px", background: "rgba(255,255,255,.14)", color: "#fff", fontSize: "16px", fontWeight: 900, cursor: "pointer" };
const gameCard = { background: "linear-gradient(180deg,#fffaf0 0%,#f9f0db 100%)", borderRadius: "18px", padding: "12px", display: "grid", gap: "12px", boxShadow: "0 12px 28px rgba(15,23,42,.10)", transition: "transform .26s cubic-bezier(.2,.8,.2,1), opacity .22s ease, box-shadow .26s ease, filter .22s ease" };
const gameCardSwitching = { transform: "translateY(10px) scale(.972)", opacity: 0.7, filter: "saturate(.82) brightness(.98)", boxShadow: "0 6px 14px rgba(15,23,42,.08)" };
const tabs = { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "7px" };
const tabPill = { border: 0, textAlign: "center", color: "#64748b", padding: "10px 4px", borderRadius: "12px", background: "#f8fafc", fontWeight: 800, fontSize: "12px", transition: "transform .22s ease, background .22s ease, color .22s ease, box-shadow .22s ease, filter .22s ease" };
const activeTab = { ...tabPill, color: "#fff", background: "linear-gradient(135deg,#0f766e,#0ea5a4)", transform: "translateY(-2px) scale(1.02)", boxShadow: "0 10px 18px rgba(14,165,164,.28)", filter: "saturate(1.08)", animation: "mkColorModePulse .42s ease" };
const topResultsCard = { display: "grid", gap: "4px" };
const topResultsHeader = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const topResultsTitle = { color: "#64748b", fontSize: "11px" };
const topResultsRow = { display: "flex", gap: "5px", overflowX: "auto", paddingBottom: "2px" };
const resultChip = { minWidth: "60px", padding: "5px 5px", borderRadius: "9px", background: "rgba(255,255,255,.62)", border: "1px solid rgba(226,232,240,.9)", display: "grid", gap: "1px", textAlign: "center", flex: "0 0 auto" };
const chipRound = { fontSize: "8px", color: "#64748b" };
const chipNumber = { fontSize: "13px" };
const viewTabs = { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "8px" };
const viewTab = { border: 0, borderRadius: "12px", background: "#f8fafc", color: "#475569", minHeight: "40px", display: "inline-flex", gap: "6px", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "12px" };
const activeViewTab = { ...viewTab, background: "#111827", color: "#fff" };
const periodRow = { display: "grid", gridTemplateColumns: "minmax(0,1fr) 96px", gap: "6px" };
const roundInfoCard = { borderRadius: "12px", background: "rgba(255,255,255,.62)", border: "1px solid rgba(226,232,240,.9)", padding: "8px 10px" };
const timerCard = { borderRadius: "12px", background: "linear-gradient(180deg,#fff4c2 0%,#f0cf65 55%,#b78618 100%)", color: "#5b3b00", padding: "8px 8px", textAlign: "center", display: "grid", gap: "1px" };
const lockedTimerCard = { ...timerCard, background: "linear-gradient(180deg,#fecaca 0%,#f87171 55%,#b91c1c 100%)", color: "#fff" };
const label = { display: "block", color: "#64748b", fontSize: "11px", marginBottom: "4px" };
const period = { display: "block", fontSize: "14px", letterSpacing: ".2px" };
const timerLabel = { fontSize: "9px", opacity: 0.82 };
const timer = { display: "inline-block", fontSize: "18px", fontWeight: 900, letterSpacing: "1px" };
const timerMeta = { fontSize: "9px", color: "#7c2d12" };
const lockedTimerMeta = { fontSize: "9px", color: "rgba(255,255,255,.82)" };
const joinRow = { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "8px" };
const greenButtonBg = "linear-gradient(145deg,#7cffaa 0%,#18b956 42%,#087734 100%)";
const violetButtonBg = "linear-gradient(145deg,#f5a3ff 0%,#b826c9 42%,#65107d 100%)";
const redButtonBg = "linear-gradient(145deg,#ff9b9b 0%,#ef4444 42%,#9f1239 100%)";
const goldButtonBg = "linear-gradient(145deg,#ffe59a 0%,#f59e0b 45%,#a85b00 100%)";
const blueButtonBg = "linear-gradient(145deg,#98d5ff 0%,#2563eb 45%,#123b9f 100%)";
const glossyButtonShadow = "inset 0 2px 4px rgba(255,255,255,.32), inset 0 -6px 10px rgba(0,0,0,.20), 0 10px 16px rgba(15,23,42,.18)";
const joinButton = { position: "relative", border: 0, borderRadius: "12px", color: "#fff", minHeight: "50px", fontWeight: 900, fontSize: "13px", display: "grid", placeItems: "center", gap: "2px", padding: "6px", boxShadow: glossyButtonShadow, textShadow: "0 1px 2px rgba(0,0,0,.22)" };
const buttonMeta = { fontSize: "10px", opacity: 0.95, fontWeight: 800 };
const numberGrid = { display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: "8px" };
const numberMeta = { fontSize: "10px", fontWeight: 800, opacity: 0.95 };
const sizeRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" };
const sizeButton = { position: "relative", border: 0, borderRadius: "12px", color: "#fff", minHeight: "46px", fontWeight: 900, fontSize: "13px", display: "grid", placeItems: "center", gap: "2px", padding: "6px", boxShadow: glossyButtonShadow, textShadow: "0 1px 2px rgba(0,0,0,.22)" };
const betStamp = { position: "absolute", right: "6px", top: "6px", background: "rgba(17,24,39,.88)", color: "#fff", borderRadius: "999px", padding: "2px 6px", fontSize: "10px", fontWeight: 900, lineHeight: 1.2 };
const recordCard = { background: "linear-gradient(180deg,#fffaf0,#f9f0db)", borderRadius: "18px", overflow: "hidden", boxShadow: "0 8px 22px rgba(15,23,42,.08)", border: "1px solid #eadfca" };
const sectionTitle = { display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "14px", color: "#64748b", fontWeight: 900, borderBottom: "1px solid #e2e8f0" };
const historyToolbar = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", padding: "10px 12px", borderBottom: "1px solid #eef2f7", background: "#fafbfd" };
const historyDateInput = { minHeight: 34, borderRadius: 10, border: "1px solid #dbeafe", background: "#fff", color: "#1e3a8a", fontWeight: 800, padding: "0 10px", fontSize: 12 };
const historyMoreButton = { border: 0, borderRadius: "999px", background: "#e0f2fe", color: "#0369a1", padding: "7px 12px", fontSize: "11px", fontWeight: 800 };
const myBetList = { display: "grid" };
const myBetRow = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "12px 14px", borderBottom: "1px solid #eef2f7" };
const mutedText = { color: "#64748b", fontSize: "12px" };
const rulesGrid = { display: "grid", gap: "12px", padding: "14px" };
const ruleBox = { display: "grid", gap: "4px", borderRadius: "14px", background: "#f8fafc", border: "1px solid #e2e8f0", padding: "12px", lineHeight: 1.55 };
const ruleBoxWide = { ...ruleBox };
const dotRow = { display: "inline-flex", gap: "4px", alignItems: "center", justifyContent: "center", flexWrap: "wrap" };
const dot = { width: "11px", height: "11px", borderRadius: "999px", display: "inline-block" };
const lockNote = { borderRadius: "12px", padding: "10px", background: "#fff7ed", color: "#c2410c", fontWeight: 800, fontSize: "13px" };
const messageBox = { borderRadius: "12px", padding: "10px", background: "#eff6ff", color: "#1d4ed8", fontWeight: 800, fontSize: "13px" };
const empty = { padding: "18px", textAlign: "center", color: "#94a3b8" };
const chipDockShell = { position: "relative", zIndex: 5, marginTop: "-1px", background: "transparent", padding: "0 0 calc(env(safe-area-inset-bottom) + 2px)", borderTop: 0 };
const chipFooter = { display: "grid", gap: "6px", background: "linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)", borderRadius: "0 0 18px 18px", padding: "8px 9px 6px", boxShadow: "0 8px 18px rgba(15,23,42,.06)", border: "1px solid #eadfca", borderTop: 0 };
const chipFooterTop = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "6px", borderRadius: "12px", background: "#f8fafc", padding: "5px 8px", boxShadow: "inset 0 1px 0 rgba(255,255,255,.75)" };
const undoButton = { border: 0, borderRadius: "999px", background: "linear-gradient(180deg,#fff7d6,#f3c952)", color: "#6b3f00", padding: "5px 8px", fontWeight: 900, fontSize: "9px", display: "inline-flex", alignItems: "center", gap: "4px", boxShadow: "0 4px 8px rgba(133,89,12,.16)" };
const footerTotalBox = { display: "grid", textAlign: "right" };
const footerTotalLabel = { fontSize: "8px", color: "#6b3f00", fontWeight: 800 };
const footerTotalValue = { fontSize: "12px", color: "#3f2600" };
const submitButton = { border: 0, borderRadius: "12px", minHeight: "38px", background: "linear-gradient(180deg,#0ea5e9 0%,#2563eb 100%)", color: "#fff", fontWeight: 900, fontSize: "12px", boxShadow: "0 6px 10px rgba(37,99,235,.18), inset 0 1px 2px rgba(255,255,255,.24)" };
const submitButtonDisabled = { ...submitButton, opacity: 0.45, boxShadow: "none" };
const chipScroller = { display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: "7px", padding: "2px 0 0", alignItems: "stretch" };
const chipButton = {
  width: "100%",
  minWidth: 0,
  height: "clamp(44px, 12vw, 62px)",
  border: 0,
  borderRadius: "999px",
  background: "transparent",
  color: "#fff7d6",
  display: "grid",
  placeItems: "center",
  padding: 0,
  overflow: "visible",
  position: "relative",
  transition: "transform .18s cubic-bezier(.2,.9,.2,1), filter .18s ease",
};
const activeChipButton = {
  transform: "translateY(-5px) scale(1.08)",
  filter: "drop-shadow(0 11px 14px rgba(133,89,12,.28))",
  zIndex: 2,
};
const chipImage = { width: "100%", height: "100%", objectFit: "contain", display: "block", borderRadius: "999px", mixBlendMode: "multiply", filter: "saturate(1.14) contrast(1.08) drop-shadow(0 7px 8px rgba(95,63,9,.22))", pointerEvents: "none" };
const poweredBy = { display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "4px", color: "#5f3b05", fontSize: "8px", fontWeight: 800, letterSpacing: ".1px", lineHeight: 1, minHeight: "13px", paddingLeft: "2px", opacity: 0.96 };
const poweredLogo = { width: "14px", height: "14px", borderRadius: "4px", objectFit: "cover", boxShadow: "0 2px 6px rgba(95,63,9,.14)" };
const poweredText = { display: "inline-flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" };
const srOnly = { position: "absolute", width: "1px", height: "1px", padding: 0, margin: "-1px", overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 };

function resolveAssetUrl(value, fallback) {
  const raw = String(value || fallback || "").trim();
  if (raw.startsWith("/uploads/")) return getMediaUrl(raw);
  return raw || fallback;
}

export default ColorGame;
